// Part 1: Core Functions
import { analyzeImageQuality, processImageQualityBatch } from './imageQualityAnalysis';
import { ImageFile } from '../types';
import { 
  getFileType, 
  getFileTypeLabel, 
  isImageFormatSupported,
  type FileType
} from './fileTypeDetection';
import { getActualOrientation, hasAnyExifData } from './exifReader';
import { 
  ValidationResult, 
  ValidationConfig, 
  DEFAULT_VALIDATION_CONFIG,
  ImageDimensions,
  getValidationConfigForFileType
} from '../types/validation';



// Import HEIC converter
import { 
  convertMultipleFiles, 
  getConversionStats,
  ConversionOptions 
} from './heicConverter';

/**
 * Loads image dimensions using browser's native image loading
 * Works for: JPEG, PNG, WebP, GIF
 */
async function getDimensionsFromImage(file: File, timeoutMs = 15000): Promise<ImageDimensions> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    let timeoutId: NodeJS.Timeout;
    
    const cleanup = () => {
      URL.revokeObjectURL(url);
      if (timeoutId) clearTimeout(timeoutId);
    };
    
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Timeout loading image: ${file.name}`));
    }, timeoutMs);
    
    img.onload = () => {
      const dimensions: ImageDimensions = {
        width: img.naturalWidth,
        height: img.naturalHeight,
        aspectRatio: img.naturalWidth / img.naturalHeight,
        isLandscape: img.naturalWidth > img.naturalHeight,
        megapixels: (img.naturalWidth * img.naturalHeight) / 1_000_000
      };
      
      cleanup();
      resolve(dimensions);
    };
    
    img.onerror = () => {
      cleanup();
      reject(new Error(`Failed to load image: ${file.name}`));
    };
    
    img.src = url;
  });
}

/**
 * Extracts dimensions from EXR file header
 * Works for: EXR files
 */
async function getDimensionsFromEXR(file: File): Promise<ImageDimensions> {
  const MAGIC_LE = 0x01312f76;
  const MAX_READ = 512 * 1024;
  let readSize = 4096;
  
  while (readSize <= MAX_READ) {
    const buffer = await file.slice(0, readSize).arrayBuffer();
    const view = new DataView(buffer);
    
    if (view.byteLength < 8) break;
    
    const magic = view.getUint32(0, true);
    if (magic !== MAGIC_LE) {
      throw new Error(`Invalid EXR magic: 0x${magic.toString(16)}`);
    }
    
    let offset = 8;
    let displayW: { w: number; h: number } | null = null;
    let dataW: { w: number; h: number } | null = null;
    let found = false;
    
    while (offset < view.byteLength) {
      // Read attribute name
      let nameStart = offset;
      while (offset < view.byteLength && view.getUint8(offset) !== 0) offset++;
      if (offset >= view.byteLength) break;
      
      const nameBytes = new Uint8Array(buffer, nameStart, offset - nameStart);
      const attributeName = new TextDecoder().decode(nameBytes);
      offset++;
      
      if (attributeName === "") {
        found = true;
        break;
      }
      
      // Read attribute type
      const typeStart = offset;
      while (offset < view.byteLength && view.getUint8(offset) !== 0) offset++;
      if (offset >= view.byteLength) break;
      
      const typeBytes = new Uint8Array(buffer, typeStart, offset - typeStart);
      const attributeType = new TextDecoder().decode(typeBytes);
      offset++;
      
      if (offset + 4 > view.byteLength) break;
      const attributeSize = view.getUint32(offset, true);
      offset += 4;
      
      if (offset + attributeSize > view.byteLength) break;
      
      const isBox2i = attributeType === "box2i" && attributeSize === 16;
      if (isBox2i && (attributeName === "displayWindow" || attributeName === "dataWindow")) {
        const xMin = view.getInt32(offset + 0, true);
        const yMin = view.getInt32(offset + 4, true);
        const xMax = view.getInt32(offset + 8, true);
        const yMax = view.getInt32(offset + 12, true);
        
        const w = xMax - xMin + 1;
        const h = yMax - yMin + 1;
        
        if (attributeName === "displayWindow") displayW = { w, h };
        else dataW = { w, h };
      }
      
      offset += attributeSize;
    }
    
    if (found || (displayW || dataW)) {
      const pick = displayW ?? dataW;
      if (pick) {
        return {
          width: pick.w,
          height: pick.h,
          aspectRatio: pick.w / pick.h,
          isLandscape: pick.w > pick.h,
          megapixels: (pick.w * pick.h) / 1_000_000
        };
      }
      break;
    }
    
    readSize *= 2;
  }
  
  throw new Error("EXR dimensions not found in header");
}

/**
 * Throws error for unsupported formats instead of using fallback
 * Forces user to provide compatible files rather than proceeding with bad data
 */
async function handleUnsupportedFormat(file: File): Promise<never> {
  const fileType = getFileType(file);
  throw new Error(
    `Cannot extract image dimensions from ${fileType.toUpperCase()} format (${file.name}). ` +
    `Supported formats: JPEG, PNG, WebP, GIF, HEIC, EXR. ` +
    `Please convert your images to a supported format before uploading.`
  );
}

/**
 * Main dimension loader using file type-based strategy pattern
 * Easily extensible for new file types
 */
export const loadImageDimensions = async (
  file: File, 
  timeoutMs = 15000
): Promise<ImageDimensions> => {
  const fileType: FileType = getFileType(file);
  
  console.log(`[ImageValidation] Loading dimensions for ${fileType.toUpperCase()}: ${file.name}`);
  
  try {
    switch (fileType) {
      case 'jpeg':
      case 'png':
      case 'webp':
      case 'gif':
        console.log(`[ImageValidation] Using browser image loading for ${fileType.toUpperCase()}`);
        return await getDimensionsFromImage(file, timeoutMs);
      
      case 'exr':
        console.log(`[ImageValidation] Using EXR header parsing`);
        return await getDimensionsFromEXR(file);
      
      case 'heic':
        console.log(`[ImageValidation] Using browser image loading for HEIC (may require conversion)`);
        return await getDimensionsFromImage(file, timeoutMs);
      
      // Future file types can be added here:
      // case 'tiff':
      //   return await getDimensionsFromTIFF(file);
      // case 'raw':
      //   return await getDimensionsFromRAW(file);
      // case 'avif':
      //   return await getDimensionsFromImage(file, timeoutMs);
      
      case 'unknown':
      default:
        console.error(`[ImageValidation] Unsupported file type: ${fileType} for file: ${file.name}`);
        return await handleUnsupportedFormat(file);
    }
  } catch (error) {
    console.error(`[ImageValidation] Failed to load dimensions for ${file.name}:`, error);
    
    // Re-throw the error instead of falling back to defaults
    // This forces the upload to fail rather than proceeding with bad data
    throw new Error(
      `Could not retrieve image dimensions from ${file.name}. ` +
      `${error instanceof Error ? error.message : 'Unknown error occurred.'} ` +
      `Cannot proceed with reconstruction without valid dimension data.`
    );
  }
};

/**
 * Type-safe dimension loader factory (optional advanced pattern)
 * Allows registering custom loaders for specific file types
 */
type DimensionLoader = (file: File, timeoutMs?: number) => Promise<ImageDimensions>;

const dimensionLoaders: Map<FileType, DimensionLoader> = new Map([
  ['jpeg', getDimensionsFromImage],
  ['png', getDimensionsFromImage],
  ['webp', getDimensionsFromImage],
  ['gif', getDimensionsFromImage],
  ['heic', getDimensionsFromImage],
  ['exr', getDimensionsFromEXR],
  // Future loaders can be registered here
]);

/**
 * Alternative implementation using registered loaders
 * Throws errors instead of fallbacks for unsupported formats
 */
export const loadImageDimensionsWithRegistry = async (
  file: File,
  timeoutMs = 15000
): Promise<ImageDimensions> => {
  const fileType: FileType = getFileType(file);
  const loader = dimensionLoaders.get(fileType);
  
  if (loader) {
    try {
      return await loader(file, timeoutMs);
    } catch (error) {
      console.error(`[ImageValidation] Loader failed for ${fileType}:`, error);
      throw new Error(
        `Failed to extract dimensions from ${fileType.toUpperCase()} file: ${file.name}. ` +
        `${error instanceof Error ? error.message : 'Unknown error occurred.'} ` +
        `Cannot proceed with reconstruction.`
      );
    }
  }
  
  // No loader available - throw error instead of fallback
  throw new Error(
    `No dimension extraction method available for ${fileType.toUpperCase()} format (${file.name}). ` +
    `Supported formats: ${Array.from(dimensionLoaders.keys()).map(t => t.toUpperCase()).join(', ')}. ` +
    `Please convert your images to a supported format.`
  );
};



/**
 * Process items in batches with concurrency control to prevent browser overload
 * Auto-tunes batch size and delays based on total item count for optimal performance
 */
const processBatches = async <T, R>(
  items: T[],
  processor: (item: T) => Promise<R>,
  batchSize?: number,
  delayMs?: number
): Promise<R[]> => {
  const results: R[] = [];
  
  // Auto-tune batch size and delay based on item count
  const totalItems = items.length;
  const autoBatchSize = batchSize || (() => {
    if (totalItems > 1000) return 8;   // Very large sets
    if (totalItems > 500) return 12;   // Large sets  
    if (totalItems > 200) return 16;   // Medium sets
    return 20;                         // Small sets
  })();
  
  const autoDelayMs = delayMs || (() => {
    if (totalItems > 1000) return 150; // Very large sets need more breathing room
    if (totalItems > 500) return 100;  // Large sets
    if (totalItems > 200) return 75;   // Medium sets
    return 50;                         // Small sets
  })();
  
  console.log(`📦 Processing ${totalItems} items in batches of ${autoBatchSize} (${autoDelayMs}ms delay)`);
  
  for (let i = 0; i < items.length; i += autoBatchSize) {
    const batch = items.slice(i, i + autoBatchSize);
    const batchNum = Math.floor(i/autoBatchSize) + 1;
    const totalBatches = Math.ceil(items.length/autoBatchSize);
    
    console.log(`🔄 Processing batch ${batchNum}/${totalBatches} (${batch.length} items)`);
    
    try {
      // Process batch with Promise.all for concurrent processing within the batch
      const batchResults = await Promise.all(
        batch.map(item => processor(item))
      );
      
      results.push(...batchResults);
      console.log(`✅ Batch ${batchNum}/${totalBatches} completed`);
      
    } catch (error) {
      console.error(`❌ Batch ${batchNum}/${totalBatches} failed:`, error);
      // Continue with remaining batches even if one fails
    }
    
    // Add delay between batches to prevent overwhelming the browser
    if (i + autoBatchSize < items.length) {
      await new Promise(resolve => setTimeout(resolve, autoDelayMs));
    }
  }
  
  console.log(`🎉 Completed processing ${totalItems} items in ${Math.ceil(items.length/autoBatchSize)} batches`);
  return results;
};

/**
 * Enhanced image processing that includes real quality analysis
 * Add this function to your existing imageValidation.ts
 */
export const processImagesWithQuality = async (
  images: ImageFile[],
  onProgress?: (progress: number, step: string) => void
): Promise<ImageFile[]> => {
  const processedImages: ImageFile[] = [];
  
  onProgress?.(0, 'Starting quality analysis...');
  
  for (let i = 0; i < images.length; i++) {
    const image = images[i];
    const progressPercent = Math.round((i / images.length) * 100);
    
    onProgress?.(progressPercent, `Analyzing ${image.file.name}...`);
    
    try {
      // Only analyze if quality hasn't been calculated yet
      if (!image.quality) {
        console.log(`🔍 Analyzing quality for ${image.file.name}...`);
        const quality = await analyzeImageQuality(image.file);
        
        processedImages.push({
          ...image,
          quality
        });
        
        console.log(`✅ Quality analysis complete for ${image.file.name}: ${quality.overall}%`);
      } else {
        processedImages.push(image);
      }
    } catch (error) {
      console.error(`❌ Failed to analyze quality for ${image.file.name}:`, error);
      
      // Keep the image but with null quality (will be handled by validation)
      processedImages.push({
        ...image,
        quality: null
      });
    }
    
    // Small delay to prevent UI blocking
    if (i % 3 === 0) {
      await new Promise(resolve => setTimeout(resolve, 10));
    }
  }
  
  onProgress?.(100, 'Quality analysis complete');
  return processedImages;
};

export const validateFileFormatAndConsistency = (
  images: ImageFile[], 
  config: ValidationConfig = DEFAULT_VALIDATION_CONFIG
): ValidationResult[] => {
  const results: ValidationResult[] = [];
  
  if (images.length === 0) {
    return results;
  }
  
  const allowedNormalizedTypes: FileType[] = ['jpeg', 'heic', 'png', 'exr'];
  const invalidFormatImages: string[] = [];
  const inconsistentTypeImages: string[] = [];
  
  // Get reference type from first valid image
  let imageSetType: FileType | null = null;

  // Process all images in one pass
  for (const image of images) {
    const normalizedType: FileType = getFileType(image.file);
    
    // Check if format is supported
    if (!allowedNormalizedTypes.includes(normalizedType)) {
      invalidFormatImages.push(image.id);
      continue; // Skip consistency check for unsupported formats
    }
    
    // Set reference type from first valid image
    if (imageSetType === null) {
      imageSetType = normalizedType;
    }
    
    // Check consistency against reference type
    if (normalizedType !== imageSetType) {
      inconsistentTypeImages.push(image.id);
    }
  }
  
  // Add format validation errors
  if (invalidFormatImages.length > 0) {
    results.push({
      isValid: false,
      category: 'blocker',
      rule: 'invalid-format',
      message: `${invalidFormatImages.length} file(s) have unsupported format`,
      affectedImages: invalidFormatImages,
      details: 'Supported formats: JPEG, HEIC, PNG, EXR. All files must be the same type.'
    });
  }
  
  // Add consistency validation errors
  if (inconsistentTypeImages.length > 0 && imageSetType) {
    const referenceLabel = getFileTypeLabel(imageSetType);
    const totalConsistent = images.length - invalidFormatImages.length - inconsistentTypeImages.length;
    
    results.push({
      isValid: false,
      category: 'blocker',
      rule: 'inconsistent-file-types',
      message: `Mixed file types detected`,
      details: `Found ${totalConsistent} ${referenceLabel} files and ${inconsistentTypeImages.length} files of different types. All files must be the same format.`,
      affectedImages: inconsistentTypeImages
    });
  }
  
  return results;
};


/**
 * Validate file sizes with detailed breakdown
 */
export const validateFileSize = (
  images: ImageFile[], 
  config: ValidationConfig = DEFAULT_VALIDATION_CONFIG
): ValidationResult[] => {
  console.log(`🔍 [FILE_SIZE] Validating ${images.length} files against ${config.maxFileSizeBytes / (1024 * 1024)}MB limit`);
  
  const results: ValidationResult[] = [];
  
  if (images.length === 0) {
    console.log(`🔍 [FILE_SIZE] No images to validate`);
    return results;
  }

  // Single filter with logging
  const oversizedImages = images.filter(img => {
    const sizeMB = (img.file.size / (1024 * 1024)).toFixed(1);
    const isOversized = img.file.size > config.maxFileSizeBytes;
    
    if (isOversized) {
      console.log(`🔍 [FILE_SIZE] ❌ OVERSIZED: ${img.file.name} - ${sizeMB}MB (limit: ${config.maxFileSizeBytes / (1024 * 1024)}MB)`);
    } else {
      console.log(`🔍 [FILE_SIZE] ✅ OK: ${img.file.name} - ${sizeMB}MB`);
    }
    
    return isOversized;
  });
  
  console.log(`🔍 [FILE_SIZE] Found ${oversizedImages.length} oversized files out of ${images.length} total`);
  
  if (oversizedImages.length > 0) {
    const maxSizeMB = Math.round(config.maxFileSizeBytes / (1024 * 1024));
    const largestSize = Math.max(...oversizedImages.map(img => img.file.size));
    const largestSizeMB = Math.round(largestSize / (1024 * 1024));
    
    const result = {
      isValid: false,
      category: 'blocker' as const,
      rule: 'file-too-large',
      message: `${oversizedImages.length} file(s) exceed ${maxSizeMB}MB limit`,
      affectedImages: oversizedImages.map(img => img.id),
      details: `Largest file: ${largestSizeMB}MB. Consider compressing images or reducing quality.`
    };
    
    console.log(`🔍 [FILE_SIZE] Creating blocker result:`, result);
    results.push(result);
  }
  
  console.log(`🔍 [FILE_SIZE] Returning ${results.length} validation results`);
  return results;
};
// Part 2: Count & Orientation Validation

/**
 * Validate minimum image count per camera with clear requirements
 */
export const validateImageCount = (
  images: ImageFile[], 
  config: ValidationConfig = DEFAULT_VALIDATION_CONFIG
): ValidationResult[] => {
  const results: ValidationResult[] = [];
  
  if (images.length === 0) {
    results.push({
      isValid: false,
      category: 'blocker',
      rule: 'no-images',
      message: 'No images uploaded',
      affectedImages: [],
      details: `Please upload at least ${config.minImagesPerCamera} images per camera`
    });
    return results;
  }
  
  // Group images by camera
  const cameraGroups = images.reduce((acc, img) => {
    const cameraId = img.cameraId || 'unknown';
    if (!acc[cameraId]) acc[cameraId] = [];
    acc[cameraId].push(img);
    return acc;
  }, {} as Record<string, ImageFile[]>);
  
  const insufficientCameras = Object.entries(cameraGroups).filter(
    ([_, imageList]) => imageList.length < config.minImagesPerCamera
  );
  
  if (insufficientCameras.length > 0) {
    const details = insufficientCameras
      .map(([camera, imageList]) => `${camera.replace('camera', 'Camera ')} has ${imageList.length} images`)
      .join(', ');
      
    results.push({
      isValid: false,
      category: 'blocker',
      rule: 'insufficient-images',
      message: `Need at least ${config.minImagesPerCamera} images per camera`,
      affectedImages: insufficientCameras.flatMap(([_, imageList]) => imageList.map(img => img.id)),
      details: `${details}. Add more images or remove cameras with too few images.`
    });
  }
  
  return results;
};



/**
 * Validates file type consistency across all images
 * Uses the first image as reference and checks all others against it
 */
export function validateFileTypeConsistency(images: ImageFile[]): ValidationResult[] {
  if (images.length === 0) return [];
  
  const results: ValidationResult[] = [];
  
  // Get reference type from first image (imageSetType)
  const imageSetType: FileType = getFileType(images[0].file);
  const inconsistentFiles: string[] = [];
  
  // Check all images against the reference type
  for (let i = 0; i < images.length; i++) {
    const image = images[i];
    const currentType: FileType = getFileType(image.file);
    
    if (currentType !== imageSetType) {
      inconsistentFiles.push(image.id);
    }
  }
  
  // Create validation error if inconsistencies found
  if (inconsistentFiles.length > 0) {
    const referenceLabel = getFileTypeLabel(imageSetType);
    const totalInconsistent = inconsistentFiles.length;
    const totalConsistent = images.length - totalInconsistent;
    
    results.push({
      isValid: false,
      category: 'blocker',
      rule: 'inconsistent-file-types',
      message: `Mixed file types detected`,
      details: `Found ${totalConsistent} ${referenceLabel} files and ${totalInconsistent} files of different types. All files must be the same format for optimal reconstruction.`,
      affectedImages: inconsistentFiles
    });
  }
  
  return results;
}

// Part 3: Resolution & Quality Validation

/**
 * Validate resolution consistency with better error reporting
 */
export const validateResolutionConsistency = async (
  images: ImageFile[], 
  config: ValidationConfig = DEFAULT_VALIDATION_CONFIG
): Promise<ValidationResult[]> => {
  const results: ValidationResult[] = [];
  
  // Group images by camera
  const cameraGroups = images.reduce((acc, img) => {
    const cameraId = img.cameraId || 'unknown';
    if (!acc[cameraId]) acc[cameraId] = [];
    acc[cameraId].push(img);
    return acc;
  }, {} as Record<string, ImageFile[]>);
  
  // Check resolution consistency for each camera
  for (const [cameraId, cameraImages] of Object.entries(cameraGroups)) {
    if (cameraImages.length < 2) continue; // Skip single image cameras
    
    console.log(`Checking resolution consistency for ${cameraId} (${cameraImages.length} images)...`);
    
    const problemImages: string[] = [];
    
    // Process images in batches to prevent browser overload
    const batchSize = Math.max(10, Math.min(25, Math.floor(100 / Object.keys(cameraGroups).length)));
    const timeout = cameraImages.length > 100 ? 20000 : 15000; // Longer timeout for large batches
    
    const loadedDimensions = await processBatches(
      cameraImages,
      async (image) => {
        try {
          const dimensions = await loadImageDimensions(image.file, timeout);
          return { ...dimensions, imageId: image.id };
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.error(`Failed to load dimensions for ${image.file.name}:`, errorMessage);
          problemImages.push(image.id);
          return null;
        }
      },
      batchSize,
      cameraImages.length > 200 ? 100 : 50 // Longer delays for very large sets
    );
    
    const validDimensions = loadedDimensions.filter(Boolean) as (ImageDimensions & { imageId: string })[];
    
    if (problemImages.length > 0) {
      results.push({
        isValid: false,
        category: 'blocker',
        rule: 'corrupted-images',
        message: `${problemImages.length} image(s) in ${cameraId.replace('camera', 'Camera ')} could not be loaded`,
        affectedImages: problemImages,
        details: 'Images may be corrupted, too large, or in an unsupported format. Try re-uploading these files.'
      });
      continue;
    }
    
    if (validDimensions.length === 0) continue;
    
    // Calculate resolution consistency
    const firstResolution = validDimensions[0];
    const inconsistentImages: string[] = [];
    const resolutionDetails: string[] = [];
    
    validDimensions.forEach((dims) => {
      const widthVariance = Math.abs(dims.width - firstResolution.width) / firstResolution.width;
      const heightVariance = Math.abs(dims.height - firstResolution.height) / firstResolution.height;
      
      if (widthVariance > config.maxResolutionVariance / 100 || 
          heightVariance > config.maxResolutionVariance / 100) {
        inconsistentImages.push(dims.imageId);
        resolutionDetails.push(`${dims.width}x${dims.height}`);
      }
    });
    
    if (inconsistentImages.length > 0) {
      const expectedRes = `${firstResolution.width}x${firstResolution.height}`;
      const foundRes = [...new Set(resolutionDetails)].join(', ');
      
      results.push({
        isValid: false,
        category: 'blocker',
        rule: 'resolution-inconsistent',
        message: `${inconsistentImages.length} image(s) in ${cameraId.replace('camera', 'Camera ')} have inconsistent resolution`,
        affectedImages: inconsistentImages,
        details: `Expected: ${expectedRes}, Found: ${foundRes}. Variance allowed: ${config.maxResolutionVariance}%. Use same camera settings for all images.`
      });
    }
  }
  
  return results;
};

// Fixed validation functions - Replace in your imageValidation.ts

/**
 * Validate image quality with batch processing to prevent infinite loops
 */
export const validateImageQuality = async (
  images: ImageFile[], 
  config: ValidationConfig = DEFAULT_VALIDATION_CONFIG
): Promise<ValidationResult[]> => {
  const results: ValidationResult[] = [];
  const lowQualityImages: string[] = [];
  const highQualityImages: string[] = [];
  const qualityDetails: { low: string[], high: string[] } = { low: [], high: [] };
  
  console.log(`Validating quality for ${images.length} images...`);
  
  // Use optimized batch processing for large image sets
  const batchSize = images.length > 1000 ? 10 : 15;
  const timeout = images.length > 500 ? 12000 : 10000;
  
  await processBatches(
    images,
    async (image) => {
      try {
        const dimensions = await loadImageDimensions(image.file, timeout);
        const megapixels = Math.round(dimensions.megapixels * 10) / 10;
        
        if (dimensions.megapixels < config.minMegapixels) {
          lowQualityImages.push(image.id);
          qualityDetails.low.push(`${image.file.name}: ${megapixels}MP`);
        } else if (dimensions.megapixels > config.maxMegapixels) {
          highQualityImages.push(image.id);
          qualityDetails.high.push(`${image.file.name}: ${megapixels}MP`);
        }
      } catch (error: unknown) {
        // Treat loading errors as low quality
        console.warn(`Failed to validate quality for ${image.file.name}:`, error);
        lowQualityImages.push(image.id);
        qualityDetails.low.push(`${image.file.name}: failed to load`);
      }
    },
    batchSize,
    images.length > 1000 ? 100 : 75
  );
  
  if (lowQualityImages.length > 0) {
    const sampleDetails = qualityDetails.low.slice(0, 3).join(', ');
    results.push({
      isValid: false,
      category: 'warning',
      rule: 'low-resolution',
      message: `${lowQualityImages.length} image(s) have low resolution`,
      affectedImages: lowQualityImages,
      details: `Examples: ${sampleDetails}${qualityDetails.low.length > 3 ? '...' : ''}. Images should be at least ${config.minMegapixels}MP for good reconstruction quality.`
    });
  }
  
  if (highQualityImages.length > 0) {
    const sampleDetails = qualityDetails.high.slice(0, 3).join(', ');
    results.push({
      isValid: false,
      category: 'warning',
      rule: 'high-resolution',
      message: `${highQualityImages.length} image(s) have very high resolution`,
      affectedImages: highQualityImages,
      details: `Examples: ${sampleDetails}${qualityDetails.high.length > 3 ? '...' : ''}. Images over ${config.maxMegapixels}MP may cause processing delays.`
    });
  }
  
  console.log(`Quality validation complete: ${results.length} issues found`);
  return results;
};



/**
 * Utility function to check if validation results allow reconstruction
 */
export const canReconstruct = (results: ValidationResult[]): boolean => {
  const hasBlockers = results.some(result => result.category === 'blocker');
  console.log(`Can reconstruct: ${!hasBlockers} (${results.filter(r => r.category === 'blocker').length} blockers, ${results.filter(r => r.category === 'warning').length} warnings)`);
  return !hasBlockers;
};

/**
 * Get a comprehensive summary of validation results
 */
export const getValidationSummary = (results: ValidationResult[]) => {
  const blockers = results.filter(r => r.category === 'blocker');
  const warnings = results.filter(r => r.category === 'warning');
  
  return {
    canProceed: blockers.length === 0,
    blockerCount: blockers.length,
    warningCount: warnings.length,
    totalIssues: results.length,
    blockers,
    warnings,
    summary: `${blockers.length} blocking issue${blockers.length === 1 ? '' : 's'}, ${warnings.length} warning${warnings.length === 1 ? '' : 's'}`
  };
};


/**
 * Complete four-phase validation pipeline with centralized file type detection
 * handle EXIF internally
 */
export const validateAllImages = async (
  images: ImageFile[], 
  config?: ValidationConfig, // Make config optional
  onProgress?: (progress: number, step: string) => void
): Promise<{ 
  results: ValidationResult[], 
  processedImages: ImageFile[] 
}> => {
  console.log(`🔍 [VALIDATION] validateAllImages called with ${images.length} images`);
  
  if (images.length === 0) {
    console.log(`🔍 [VALIDATION] No images provided, returning early`);
    return {
      results: [{
        isValid: false,
        category: 'blocker',
        rule: 'no-images',
        message: 'No images to validate',
        affectedImages: [],
        details: 'Please upload at least one image to continue'
      }],
      processedImages: images
    };
  }
  
  // Step 1: Auto-detect file type and get appropriate config (5% progress)
  onProgress?.(5, 'Analyzing dataset characteristics...');
  
  const datasetFileType: FileType = getFileType(images[0].file);

  // Use file-type-specific config if no config provided
  const validationConfig = config || getValidationConfigForFileType(datasetFileType);
  // ADD THIS DEBUG LOG:
console.log(`🔧 [DEBUG] File type detected: ${datasetFileType}`);
console.log(`🔧 [DEBUG] Config provided: ${!!config}`);
console.log(`🔧 [DEBUG] Validation config max size: ${validationConfig.maxFileSizeBytes / (1024 * 1024)}MB`);
console.log(`🔧 [DEBUG] getValidationConfigForFileType function: ${typeof getValidationConfigForFileType}`);

  console.log(`🔍 [VALIDATION] Dataset analysis:`, {
    fileType: datasetFileType,
    imageCount: images.length,
    configType: (validationConfig as any).displayName || 'Custom',
    maxFileSize: `${validationConfig.maxFileSizeBytes / (1024 * 1024)}MB`
  });
  
  // Check if dataset has EXIF data
  const hasExif = await hasAnyExifData(images);
  
  console.log(`🔍 [VALIDATION] EXIF data detected:`, hasExif);
  
  let allResults: ValidationResult[] = [];
  let processedImages = images;
  
  try {
    // Phase 1: Basic validation (5-30%) - using file-type-specific config
    onProgress?.(10, 'Starting basic validation...');
    const phase1Results = await validatePhase1Basic(images, validationConfig, (progress, step) => {
      const adjustedProgress = 10 + ((progress / 100) * 20); // Scale to 10-30%
      onProgress?.(adjustedProgress, step);
    });
    allResults.push(...phase1Results);
    
    // Check if basic validation failed
    const hasPhase1Blockers = phase1Results.some(result => result.category === 'blocker');
    if (hasPhase1Blockers) {
      console.log('🔍 [VALIDATION] Phase 1 blockers found, stopping pipeline');
      onProgress?.(100, 'Validation stopped due to basic errors');
      return { results: allResults, processedImages: images };
    }
    
    // Phase 2: Orientation validation (30-55%) - Skip for EXR/PNG or no EXIF
    onProgress?.(30, 'Checking orientation requirements...');
    
    if (!hasExif || datasetFileType === 'exr' || datasetFileType === 'png') {
      console.log(`🔍 [VALIDATION] Skipping orientation validation for ${datasetFileType.toUpperCase()} or no EXIF data`);
      onProgress?.(55, `Skipped orientation check - ${datasetFileType.toUpperCase()} files have no orientation metadata`);
    } else {
      console.log(`🔍 [VALIDATION] EXIF data detected - running orientation validation`);
      const phase2Results = await validatePhase2Orientation(images, datasetFileType, validationConfig, (progress, step) => {
        const adjustedProgress = 30 + ((progress / 100) * 25); // Scale to 30-55%
        onProgress?.(adjustedProgress, step);
      });
      allResults.push(...phase2Results);
      
      // Check if orientation validation failed critically
      const hasPhase2Blockers = phase2Results.some(result => result.category === 'blocker');
      if (hasPhase2Blockers) {
        console.log('🔍 [VALIDATION] Phase 2 orientation blockers found, stopping pipeline');
        onProgress?.(100, 'Validation stopped due to orientation errors');
        return { results: allResults, processedImages: images };
      }
    }
    
    // Phase 3: Image conversion for pipeline (55-80%) - Only for HEIC
    onProgress?.(55, 'Checking for image conversion needs...');
    processedImages = await validatePhase3ImageConversion(processedImages, datasetFileType, (progress, step) => {
      const adjustedProgress = 55 + ((progress / 100) * 25); // Scale to 55-80%
      onProgress?.(adjustedProgress, step);
    });
    
    // Phase 4: Resolution consistency validation (80-100%)
    onProgress?.(80, 'Checking resolution consistency...');
    
    if (!hasExif && (datasetFileType === 'exr' || datasetFileType === 'png')) {
      console.log(`🔍 [VALIDATION] Skipping resolution validation for ${datasetFileType.toUpperCase()} dataset`);
      onProgress?.(100, `Validation complete - ${datasetFileType.toUpperCase()} dataset processed`);
    } else {
      const phase4Results = await validatePhase4ResolutionConsistency(processedImages, validationConfig, (progress, step) => {
        const adjustedProgress = 80 + ((progress / 100) * 20); // Scale to 80-100%
        onProgress?.(adjustedProgress, step);
      });
      allResults.push(...phase4Results);
    }
    
    // Final summary
    const summary = getValidationSummary(allResults);
    console.log(`🔍 [VALIDATION] Validation complete:`, {
      fileType: datasetFileType,
      configUsed: (validationConfig as any).displayName || 'Custom',
      hasExifData: hasExif,
      canProceed: summary.canProceed,
      blockers: summary.blockerCount,
      warnings: summary.warningCount,
      totalIssues: allResults.length
    });
    
    onProgress?.(100, hasExif ? 'Validation complete' : `Basic validation complete - ${datasetFileType.toUpperCase()} dataset processed`);
    
    return {
      results: allResults,
      processedImages
    };
    
  } catch (error: unknown) {
    console.error('🔍 [VALIDATION] Validation pipeline failed:', error);
    
    return {
      results: [{
        isValid: false,
        category: 'blocker',
        rule: 'validation-error',
        message: 'Validation pipeline failed',
        affectedImages: [],
        details: error instanceof Error ? error.message : 'Unknown error during validation. Please try again.'
      }],
      processedImages: images
    };
  }
};

/**
 * Four-phase validation pipeline with proper ordering
 */

/**
 * Phase 1: Basic validation checks (fast, no image loading required)
 */
export const validatePhase1Basic = async (
  images: ImageFile[], 
  config: ValidationConfig = DEFAULT_VALIDATION_CONFIG,
  onProgress?: (progress: number, step: string) => void
): Promise<ValidationResult[]> => {
  console.log(`🔍 [PHASE1] Starting basic validation for ${images.length} images`);
  console.log(`🔍 [PHASE1] Using config:`, {
    maxFileSizeMB: config.maxFileSizeBytes / (1024 * 1024),
    allowedFormats: config.allowedFormats
  });
  if (images.length === 0) {
    return [{
      isValid: false,
      category: 'blocker',
      rule: 'no-images',
      message: 'No images to validate',
      affectedImages: [],
      details: 'Please upload at least one image to continue'
    }];
  }
  
  console.log(`Starting Phase 1 basic validation for ${images.length} images...`);
  const allResults: ValidationResult[] = [];
  
  try {
    // Step 1: File type consistency check (all files must be same type)
    onProgress?.(25, 'Checking file type consistency...');
    console.log('Step 1: Validating file type consistency...');
    const typeResults = validateFileTypeConsistency(images);
    allResults.push(...typeResults);
    console.log(`File type consistency: ${typeResults.length} issues found`);
    
    // Step 2: Image count validation (quick)
    onProgress?.(50, 'Checking image counts...');
    console.log('Step 2: Validating image counts...');
    const countResults = validateImageCount(images, config);
    allResults.push(...countResults);
    console.log(`Image count validation: ${countResults.length} issues found`);

    console.log(`🔍 [PHASE1] Starting basic validation for ${images.length} images`);
  console.log(`🔍 [PHASE1] Using config:`, {
    maxFileSizeMB: config.maxFileSizeBytes / (1024 * 1024),
    allowedFormats: config.allowedFormats
  });
    
    // Step 3: File size validation (quick)
    onProgress?.(75, 'Checking file sizes...');
    console.log('Step 3: Validating file sizes...');
    const sizeResults = validateFileSize(images, config);
    allResults.push(...sizeResults);
    console.log(`File size validation: ${sizeResults.length} issues found`);
    
    onProgress?.(100, 'Phase 1 basic validation complete');
    console.log(`Phase 1 basic validation complete. Total issues: ${allResults.length}`);
    
  } catch (error: unknown) {
    console.error('Phase 1 validation failed:', error);
    allResults.push({
      isValid: false,
      category: 'blocker',
      rule: 'validation-error',
      message: 'Basic validation failed',
      affectedImages: [],
      details: error instanceof Error ? error.message : 'Unknown error during basic validation.'
    });
  }
  
  return allResults;
};

/**
 * Phase 2: Orientation validation (accepts pre-computed file type)
 */
export const validatePhase2Orientation = async (
  images: ImageFile[], 
  datasetFileType: FileType,
  config: ValidationConfig = DEFAULT_VALIDATION_CONFIG,
  onProgress?: (progress: number, step: string) => void
): Promise<ValidationResult[]> => {
  console.log(`Starting Phase 2 orientation validation for ${images.length} ${datasetFileType.toUpperCase()} images...`);
  
  // Check if we should skip orientation validation based on file type
  if (datasetFileType === 'exr' || datasetFileType === 'png') {
    console.log(`${datasetFileType.toUpperCase()} files detected - skipping orientation validation (no EXIF data available)`);
    onProgress?.(100, `Skipped orientation check - ${datasetFileType.toUpperCase()} files have no orientation metadata`);
    return [];
  }
  
  onProgress?.(25, 'Checking orientation consistency...');
  console.log(`Validating orientation for ${datasetFileType.toUpperCase()} files...`);
  
  try {
    const orientationResults = await validateOrientationConsistency(images, config);
    
    onProgress?.(100, 'Phase 2 orientation validation complete');
    console.log(`Phase 2 orientation validation complete. ${orientationResults.length} issues found`);
    
    return orientationResults;
    
  } catch (error: unknown) {
    console.error('Phase 2 orientation validation failed:', error);
    return [{
      isValid: false,
      category: 'blocker',
      rule: 'validation-error',
      message: 'Orientation validation failed',
      affectedImages: [],
      details: error instanceof Error ? error.message : 'Unknown error during orientation validation.'
    }];
  }
};

/**
 * Phase 3: Image conversion for pipeline (accepts pre-computed file type)
 */
export const validatePhase3ImageConversion = async (
  images: ImageFile[],
  datasetFileType: FileType,
  onProgress?: (progress: number, step: string) => void
): Promise<ImageFile[]> => {
  console.log(`Starting Phase 3 image conversion for ${datasetFileType.toUpperCase()} files...`);
  
  if (datasetFileType !== 'heic') {
    console.log(`Images are ${datasetFileType.toUpperCase()} format, no conversion needed`);
    onProgress?.(100, `No conversion needed - ${datasetFileType.toUpperCase()} format`);
    return images;
  }
  
  console.log(`Converting all ${images.length} HEIC files to JPEG...`);
  
  try {
    // Use your existing converter with optimal settings
    const convertedFiles = await convertMultipleFiles(
      images.map(img => img.file),
      {
        quality: 0.95,
        preserveMetadata: true, // Critical for orientation data
        format: 'JPEG'
      },
      (progress, currentFile, convertedCount) => {
        onProgress?.(
          10 + (progress * 0.8),
          `Converting: ${currentFile} (${convertedCount}/${images.length})`
        );
      }
    );
    
    // Simple 1:1 mapping since all files are HEIC
    const updatedImages = images.map((imageFile, index) => {
      URL.revokeObjectURL(imageFile.preview); // Cleanup
      return {
        ...imageFile,
        file: convertedFiles[index],
        preview: URL.createObjectURL(convertedFiles[index])
      };
    });
    
    onProgress?.(100, `Conversion complete - ${images.length} HEIC files converted`);
    return updatedImages;
    
  } catch (error) {
    console.error('HEIC conversion failed:', error);
    onProgress?.(100, 'Conversion failed - using original files');
    return images; // Fallback
  }
};

/**
 * Phase 4: Resolution consistency validation
 */
export const validatePhase4ResolutionConsistency = async (
  images: ImageFile[], 
  config: ValidationConfig = DEFAULT_VALIDATION_CONFIG,
  onProgress?: (progress: number, step: string) => void
): Promise<ValidationResult[]> => {
  console.log(`Starting Phase 4 resolution consistency validation for ${images.length} images...`);
  
  try {
    onProgress?.(25, 'Checking resolution consistency...');
    console.log('Validating resolution consistency...');
    
    const resolutionResults = await validateResolutionConsistency(images, config);
    
    onProgress?.(100, 'Phase 4 resolution validation complete');
    console.log(`Phase 4 resolution validation complete. ${resolutionResults.length} issues found`);
    
    return resolutionResults;
    
  } catch (error: unknown) {
    console.error('Phase 4 resolution validation failed:', error);
    return [{
      isValid: false,
      category: 'blocker',
      rule: 'validation-error',
      message: 'Resolution validation failed',
      affectedImages: [],
      details: error instanceof Error ? error.message : 'Unknown error during resolution validation.'
    }];
  }
};
/**
 * Enhanced orientation validation that checks for consistency first, then suggests landscape
 */
export const validateOrientationConsistency = async (
  images: ImageFile[], 
  config: ValidationConfig = DEFAULT_VALIDATION_CONFIG
): Promise<ValidationResult[]> => {
  const results: ValidationResult[] = [];
  const orientationData: { id: string, isLandscape: boolean, width: number, height: number }[] = [];
  const invalidMetadataImages: string[] = [];
  const errorDetails: string[] = [];
  
  console.log(`Validating orientation consistency for ${images.length} images...`);
  
  // Use batched processing for better performance
  const batchSize = images.length > 500 ? 15 : 20;
  const delayMs = images.length > 500 ? 75 : 50;
  
  await processBatches(
    images,
    async (image) => {
      try {
        const orientation = await getActualOrientation(image.file);
        orientationData.push({
          id: image.id,
          isLandscape: orientation.isLandscape,
          width: orientation.width,
          height: orientation.height
        });
        console.log(`Image ${image.file.name}: ${orientation.width}x${orientation.height}, landscape: ${orientation.isLandscape}`);
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.warn(`Invalid metadata for image ${image.file.name}:`, errorMessage);
        invalidMetadataImages.push(image.id);
        errorDetails.push(`${image.file.name}: ${errorMessage}`);
      }
    },
    batchSize,
    delayMs
  );
  
  // Check for metadata errors first
  if (invalidMetadataImages.length > 0) {
    results.push({
      isValid: false,
      category: 'blocker',
      rule: 'invalid-metadata',
      message: `${invalidMetadataImages.length} image(s) have corrupted or missing metadata`,
      affectedImages: invalidMetadataImages,
      details: `Issues found: ${errorDetails.slice(0, 3).join('; ')}${errorDetails.length > 3 ? '...' : ''}. Try re-saving images or using different files.`
    });
    return results; // Don't proceed with orientation checks if metadata is corrupted
  }
  
  if (orientationData.length === 0) {
    return results; // No valid data to check
  }
  
  // Check orientation consistency
  const landscapeImages = orientationData.filter(img => img.isLandscape);
  const portraitImages = orientationData.filter(img => !img.isLandscape);
  
  if (landscapeImages.length > 0 && portraitImages.length > 0) {
    // Mixed orientations - this is a blocker
    results.push({
      isValid: false,
      category: 'blocker',
      rule: 'mixed-orientations',
      message: `Mixed orientations detected: ${landscapeImages.length} landscape, ${portraitImages.length} portrait`,
      affectedImages: portraitImages.map(img => img.id), // Mark portrait as problematic
      details: `All images must have the same orientation. Either use all landscape or all portrait images for consistent reconstruction.`
    });
  } else if (portraitImages.length === orientationData.length) {
    // All portrait - consistent but not optimal
    results.push({
      isValid: false,
      category: 'warning',
      rule: 'all-portrait-orientation',
      message: `All ${portraitImages.length} images are in portrait orientation`,
      affectedImages: portraitImages.map(img => img.id),
      details: `While consistent, landscape images typically produce better 3D reconstructions due to wider field of view. Consider retaking photos in landscape mode if possible.`
    });
  }
  // If all landscape, that's ideal - no error needed
  
  return results;
};
/**
 * Helper function to check if a file is HEIC
 */
function isHeicFile(file: File): boolean {
  // Check MIME type first
  const mimeType = file.type.toLowerCase();
  if (mimeType === 'image/heic' || mimeType === 'image/heif') {
    return true;
  }
  
  // Fallback to extension check
  const extension = file.name.toLowerCase().split('.').pop();
  return extension === 'heic' || extension === 'heif';
}
